/// <reference types="vite/client" />
import axios, {
  AxiosRequestConfig,
  AxiosHeaders,
  AxiosResponse,
  AxiosError,
} from 'axios';

// Auth key constant for localStorage
export const AUTH_KEY = 'auth';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  headers: AxiosHeaders;
  _retry?: boolean;
}

export interface AxiosResponseData<T> {
  data: T;
  status: number;
  statusText: string;
  headers: AxiosHeaders;
  config: CustomAxiosRequestConfig;
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Middleware: Response Interceptor
axiosInstance.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    // Authorization
    const auth = JSON.parse(localStorage.getItem(AUTH_KEY) || '{}');
    if (auth?.access_token) {
      config.headers['Authorization'] = `Bearer ${auth.access_token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Middleware: Response Interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    // If error is 401 and we haven't retried yet
    if (
      !originalRequest.url?.includes('login') &&
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // Get refresh token from localStorage
        const auth = JSON.parse(localStorage.getItem(AUTH_KEY) || '{}');
        if (!auth?.refresh_token) {
          throw new Error('セッションが終了しました。再度ログインをお願いします。');
        }

        // Call refresh token API
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/access-token`,
          { refresh_token: auth.refresh_token },
        );

        const { access_token } = response.data;

        // Get current auth data and update access_token
        const currentAuth = JSON.parse(localStorage.getItem(AUTH_KEY) || '{}');
        const updatedAuth = {
          ...currentAuth,
          access_token,
        };

        // Save updated auth data
        localStorage.setItem(AUTH_KEY, JSON.stringify(updatedAuth));

        // Update authorization header
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;

        // Retry the original request
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // If refresh token fails, clear storage and redirect to login
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
